import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
    LinkedMerchandisedProductType,
    LinkedSizeAttributesType,
    PanelPlacementType,
} from 'src/types/decorationSpecs';
import { changeEditorSkuSuccess, clearEditorSku } from 'src/pages/editor/components/surfaces/slice';
import type { GenericSelectorProductType } from 'src/types/product';

export type DecorationSpecsSliceState = {
    /** Identity of the product/selection the current data (or in-flight fetch) applies to —
     * `${cimId}-${cimVersion}-${JSON.stringify(variables)}`. Used to skip redundant refetches and
     * to ignore stale responses from a superseded request. Covers `decorationPlacement`/
     * `printWhite` (DIS) only. */
    cacheKey: string | undefined;
    isLoading: boolean;
    /** `null` means DIS confirmed there's no data for this product (e.g. non-apparel) — distinct
     * from not having fetched yet (`cacheKey === undefined`). */
    decorationPlacement: PanelPlacementType[] | null;
    printWhite: boolean | null;
    /** Cache key for the fetched product model definition — `${effectiveSku}-${effectiveVersion}`. */
    productModelKey: string | undefined;
    /** The raw product definition fetched from api.products.cimpress.io (containing options and property assignments
     * for all sizes). Used by selectors (`getSizeChart`, `getBodyMeasurements`) to derive size chart and
     * body width/length dynamically against the current merchantVariables. */
    productModel: GenericSelectorProductType | null;
    // TODO(linked-product-fallback): remove `linkedProduct`/`linkedSizeAttributesKey`/
    // `linkedSizeAttributes` below, and the reducers that write them, alongside the rest of this
    // fallback — see `linkedMerchandisedProductClient.ts`'s module doc.
    /** The merchandised (PRD) product linked to the current CIM, resolved as a fallback source
     * for body Width/Length when `productModel` doesn't declare them itself (see
     * `productPropertySizeChart.ts`'s module doc). Guarded by the same `productModelKey` as
     * `productModel` — they describe the same CIM identity. `undefined` means "not attempted yet
     * for this `productModelKey`"; `null` means "attempted, none found (or none needed)". */
    linkedProduct: LinkedMerchandisedProductType | null | undefined;
    /** Cache key for `linkedSizeAttributes` — `${productId}-${version}-${size}`. */
    linkedSizeAttributesKey: string | undefined;
    /** Width/Length resolved from `linkedProduct` for one specific size — see
     * `LinkedSizeAttributesType`'s own doc comment for why it's scoped to a single size. */
    linkedSizeAttributes: LinkedSizeAttributesType | null;
    /** Options DIS itself reports as still unset (via a 400 response) that are needed before
     * `decorationPlacement`/`printWhite` can be trusted — populated instead of those fields when
     * this is why they're absent, so the UI can prompt for a selection rather than showing "not
     * available" or a possibly-wrong single answer. See `disClient.ts`'s `retrieveDesignData`. */
    decorationMissingSelections: string[] | null;
};

export const initialState: DecorationSpecsSliceState = {
    cacheKey: undefined,
    isLoading: false,
    decorationPlacement: null,
    printWhite: null,
    productModelKey: undefined,
    productModel: null,
    linkedProduct: undefined,
    linkedSizeAttributesKey: undefined,
    linkedSizeAttributes: null,
    decorationMissingSelections: null,
};

type RetrieveDecorationSpecsPayload = { cacheKey: string };

type RetrieveDecorationSpecsSettledPayload = {
    cacheKey: string;
    decorationPlacement: PanelPlacementType[] | null;
    printWhite: boolean | null;
    decorationMissingSelections: string[] | null;
};

type DecorationSpecsFetchFailedPayload = { cacheKey: string };

type RequestProductModelPayload = { productModelKey: string };

type ProductModelResolvedPayload = {
    productModelKey: string;
    productModel: GenericSelectorProductType | null;
};

type ProductModelFetchFailedPayload = { productModelKey: string };

type LinkedProductResolvedPayload = {
    productModelKey: string;
    linkedProduct: LinkedMerchandisedProductType | null;
};

type RequestLinkedSizeAttributesPayload = { linkedSizeAttributesKey: string };

type LinkedSizeAttributesResolvedPayload = {
    linkedSizeAttributesKey: string;
    linkedSizeAttributes: LinkedSizeAttributesType | null;
};

type ClearLinkedSizeAttributesPayload = { linkedSizeAttributesKey: string | undefined };

const slice = createSlice({
    name: 'decorationSpecs',
    initialState,
    reducers: {
        retrieveDecorationSpecs: (state, { payload }: PayloadAction<RetrieveDecorationSpecsPayload>) => {
            state.cacheKey = payload.cacheKey;
            state.isLoading = true;
            // Clear the previous DIS result immediately, not just when the new one settles. The
            // previous result describes the *old* selection — if a designer removes or changes an
            // option that made it valid (e.g. clearing `Backside` after DIS had already answered
            // for a fully-specified selection), that answer is no longer trustworthy and must not
            // keep rendering as if it still applies for however long this fetch takes.
            //
            // Deliberately does NOT clear `productModel` — it stays available across a DIS refetch.
            state.decorationPlacement = null;
            state.printWhite = null;
            state.decorationMissingSelections = null;
        },
        retrieveDecorationSpecsSettled: (state, { payload }: PayloadAction<RetrieveDecorationSpecsSettledPayload>) => {
            // Guard against a slower, now-superseded request resolving after a newer selection
            // change already started a different fetch.
            if (state.cacheKey !== payload.cacheKey) {
                return;
            }
            state.isLoading = false;
            state.decorationPlacement = payload.decorationPlacement;
            state.printWhite = payload.printWhite;
            state.decorationMissingSelections = payload.decorationMissingSelections;
        },
        /**
         * Resets `cacheKey` back to `undefined` when the DIS fetch itself *failed* (a real,
         * non-404/400 error — see `disClient.ts`'s own 404/400 handling, which resolves normally
         * rather than throwing). Dispatched instead of `retrieveDecorationSpecsSettled` for this
         * case: without it, a transient failure would be cached as a settled "no data" answer
         * indistinguishable from a genuine 404, and the `cacheKey` guard in `fetchDecorationSpecs`
         * would then treat the failed attempt as already up to date — permanently skipping a retry
         * until some variable changes, even if the designer picks the exact same selection again
         * later. `decorationPlacement`/`printWhite`/`decorationMissingSelections` are left exactly
         * as `retrieveDecorationSpecs` already cleared them (`null`) — nothing was resolved.
         */
        decorationSpecsFetchFailed: (state, { payload }: PayloadAction<DecorationSpecsFetchFailedPayload>) => {
            if (state.cacheKey !== payload.cacheKey) {
                return;
            }
            state.isLoading = false;
            state.cacheKey = undefined;
        },
        /**
         * Marks a product model fetch as in flight for `productModelKey`, set optimistically
         * *before* the request resolves — mirrors `retrieveDecorationSpecs`'s `cacheKey` handling.
         * This lets `productModelResolved` reject a slower, now-superseded response the same way
         * `retrieveDecorationSpecsSettled` already rejects a superseded `cacheKey`: without it, two
         * overlapping fetches (e.g. two rapid SKU changes before the first request resolves) could
         * resolve out of order and leave a stale product model — and the body measurements derived
         * from it — applied to whatever SKU is currently selected.
         */
        requestProductModel: (state, { payload }: PayloadAction<RequestProductModelPayload>) => {
            state.productModelKey = payload.productModelKey;
            // Clear the previous model immediately, not just when the new one settles — it
            // describes a different product identity now that `productModelKey` has changed.
            state.productModel = null;
            // TODO(linked-product-fallback): remove these 3 lines alongside the rest of this
            // fallback — see `linkedMerchandisedProductClient.ts`'s module doc.
            // The linked-merchandised-product fallback (see `linkedProduct`'s own doc comment)
            // also describes this now-superseded CIM identity — reset it alongside `productModel`
            // rather than leaving a previous product's fallback data (or in-flight lookup)
            // attached to whatever CIM is selected next.
            state.linkedProduct = undefined;
            state.linkedSizeAttributesKey = undefined;
            state.linkedSizeAttributes = null;
        },
        /**
         * Sets the product model definition fetched from `api.products.cimpress.io`.
         */
        productModelResolved: (state, { payload }: PayloadAction<ProductModelResolvedPayload>) => {
            // Guard against a slower, now-superseded request resolving after a newer product/version
            // change already started a different fetch (see `requestProductModel`).
            if (state.productModelKey !== payload.productModelKey) {
                return;
            }
            state.productModel = payload.productModel;
        },
        /**
         * Resets `productModelKey` back to `undefined` when the product-model fetch itself
         * *failed* (a real network/server error — `fetchProductModel` already converts a 404 into
         * a confirmed `productModel: null` via `productModelResolved`, so this is only for the
         * other case). Also dispatched by `fetchLinkedProductIfNeeded` when *its* own
         * linked-product lookup fails, even though the product-model fetch itself succeeded — see
         * that function's own comment for why reusing this (rather than a narrower
         * `linkedProduct`-only reset) is the simplest safe way to get a working retry there without
         * reintroducing the stale-state-across-concurrent-executions race `fetchLinkedProductIfNeeded`
         * was changed to avoid.
         *
         * Without this, a failure would be cached as a settled "no product model" (or "no linked
         * product") answer indistinguishable from a confirmed negative, and the `productModelKey`
         * guard in `fetchProductModelIfNeeded` would treat the failed attempt as already resolved —
         * permanently skipping a retry until the CIM/version itself changes. Resetting the key
         * means the next `changeEditorSkuSuccess`/`RELOAD` for this exact CIM/version retries the
         * whole chain instead.
         *
         * Guarded like every other writer here: only resets if nothing newer has already
         * superseded this key.
         */
        productModelFetchFailed: (state, { payload }: PayloadAction<ProductModelFetchFailedPayload>) => {
            if (state.productModelKey !== payload.productModelKey) {
                return;
            }
            state.productModelKey = undefined;
        },
        /**
         * Sets the merchandised (PRD) product resolved as the linked-Width/Length fallback for
         * the CIM identified by `productModelKey` (`null` when none was found, or none was
         * needed) — mirrors `productModelResolved`'s staleness guard, since this describes the
         * same CIM identity `productModel` does.
         */
        linkedProductResolved: (state, { payload }: PayloadAction<LinkedProductResolvedPayload>) => {
            if (state.productModelKey !== payload.productModelKey) {
                return;
            }
            state.linkedProduct = payload.linkedProduct;
        },
        /**
         * Marks a linked-size-attributes fetch as in flight for `linkedSizeAttributesKey`, set
         * optimistically before the request resolves — mirrors `requestProductModel`'s own
         * staleness-guard pattern.
         */
        requestLinkedSizeAttributes: (state, { payload }: PayloadAction<RequestLinkedSizeAttributesPayload>) => {
            state.linkedSizeAttributesKey = payload.linkedSizeAttributesKey;
            state.linkedSizeAttributes = null;
        },
        /** Sets Width/Length resolved via Selector API against the linked merchandised product. */
        linkedSizeAttributesResolved: (state, { payload }: PayloadAction<LinkedSizeAttributesResolvedPayload>) => {
            if (state.linkedSizeAttributesKey !== payload.linkedSizeAttributesKey) {
                return;
            }
            state.linkedSizeAttributes = payload.linkedSizeAttributes;
        },
        /**
         * Clears a previously-resolved `linkedSizeAttributes` when there's nothing current to
         * resolve it for (`Size` was removed, or the fallback isn't needed/available), or when the
         * generator that set `linkedSizeAttributesKey` was cancelled mid-flight — without this,
         * removing `Size` would leave the last-resolved value displayed as if it still applied to
         * the current selection (the same failure mode `retrieveDecorationSpecs`'s own
         * immediate-clear handling exists to avoid for decoration placement/white ink).
         *
         * Guarded by `linkedSizeAttributesKey` like every other writer to this state — without the
         * guard, this reducer running for a now-superseded key (e.g. a cancelled `takeLatest` run
         * clearing after a *different*, concurrently-running `takeEvery` chain has already set a
         * new, legitimate key for a different CIM) would wipe out that other chain's still-valid
         * data. The caller passes whatever key *it* observed/owns, not necessarily the current
         * state's key — if they no longer match, nothing is cleared.
         */
        clearLinkedSizeAttributes: (state, { payload }: PayloadAction<ClearLinkedSizeAttributesPayload>) => {
            if (state.linkedSizeAttributesKey !== payload.linkedSizeAttributesKey) {
                return;
            }
            state.linkedSizeAttributesKey = undefined;
            state.linkedSizeAttributes = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(clearEditorSku, () => initialState).addCase(changeEditorSkuSuccess, () => initialState);
    },
});

export const reducer = slice.reducer;
export const {
    retrieveDecorationSpecs,
    retrieveDecorationSpecsSettled,
    decorationSpecsFetchFailed,
    requestProductModel,
    productModelResolved,
    productModelFetchFailed,
    linkedProductResolved,
    requestLinkedSizeAttributes,
    linkedSizeAttributesResolved,
    clearLinkedSizeAttributes,
} = slice.actions;
